/**
 * The table used by every list screen.
 *
 * Design notes that are not obvious:
 *  · Tables are ELEVATED surfaces, so `text-content-tertiary` is legal inside them.
 *  · Numeric columns get `.numeric` (tabular figures, right aligned). Financial columns
 *    that do not line up are unreadable at a glance and invite transcription errors.
 *  · Below `md` the table becomes a stack of cards. A 12-column AR ageing report inside a
 *    horizontal scroller on a tablet is technically responsive and practically useless.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import { cn } from '@frontend/lib/cn';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer. Receives the whole row. */
  cell: (row: T) => ReactNode;
  /** Right-aligned, tabular figures. */
  numeric?: boolean;
  /** Sort comparator. Omit to make the column unsortable. */
  sort?: (a: T, b: T) => number;
  /** Hidden below `md`, where the table stacks. Use for secondary columns. */
  hideOnMobile?: boolean;
  width?: string;
  className?: string;
}

export interface DataTableProps<T> {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  /** Row click target — makes the whole row activatable by keyboard too. */
  onRowClick?: (row: T) => void;
  /** Selection support, for bulk approve. */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
    isSelectable?: (row: T) => boolean;
  };
  density?: 'compact' | 'comfortable';
  emptyState?: ReactNode;
  className?: string;
  testIdPrefix?: string;
  /** A totals / summary row rendered in `tfoot`. */
  footer?: ReactNode;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selection,
  density = 'compact',
  emptyState,
  className,
  testIdPrefix = 'row',
  footer,
}: DataTableProps<T>): JSX.Element {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [descending, setDescending] = useState(false);

  const sorted = useMemo(() => {
    if (sortKey === null) return rows;
    const column = columns.find((candidate) => candidate.key === sortKey);
    if (column?.sort === undefined) return rows;
    const comparator = column.sort;
    const copy = [...rows];
    copy.sort((a, b) => (descending ? -comparator(a, b) : comparator(a, b)));
    return copy;
  }, [rows, columns, sortKey, descending]);

  const selectableRows = useMemo(
    () => (selection === undefined ? [] : rows.filter((row) => selection.isSelectable?.(row) ?? true)),
    [rows, selection],
  );
  const allSelected =
    selection !== undefined && selectableRows.length > 0 && selectableRows.every((row) => selection.selected.has(rowKey(row)));

  function toggleSort(column: Column<T>): void {
    if (column.sort === undefined) return;
    if (sortKey === column.key) {
      setDescending((previous) => !previous);
      return;
    }
    setSortKey(column.key);
    setDescending(false);
  }

  if (rows.length === 0 && emptyState !== undefined) return <>{emptyState}</>;

  return (
    <div className={cn('w-full', className)}>
      {/* ── Table, md and up ─────────────────────────────────────────── */}
      <div className="hidden overflow-x-auto tablet:block">
        <table className="data-table" data-density={density}>
          <thead>
            <tr>
              {selection !== undefined && (
                <th scope="col" className="w-9 px-2">
                  <input
                    type="checkbox"
                    aria-label={allSelected ? 'Clear selection' : 'Select all rows'}
                    checked={allSelected}
                    onChange={() => {
                      const next = new Set<string>(selection.selected);
                      if (allSelected) {
                        for (const row of selectableRows) next.delete(rowKey(row));
                      } else {
                        for (const row of selectableRows) next.add(rowKey(row));
                      }
                      selection.onChange(next);
                    }}
                    className="h-3.5 w-3.5 cursor-pointer accent-interactive"
                    data-testid="select-all"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(column.numeric === true && 'numeric', column.className)}
                  style={column.width === undefined ? undefined : { width: column.width }}
                  aria-sort={
                    sortKey === column.key ? (descending ? 'descending' : 'ascending') : undefined
                  }
                >
                  {column.sort === undefined ? (
                    column.header
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleSort(column)}
                      className={cn(
                        'inline-flex items-center gap-1 uppercase tracking-[0.04em] hover:text-content',
                        column.numeric === true && 'flex-row-reverse',
                      )}
                    >
                      {column.header}
                      {sortKey !== column.key ? (
                        <ChevronsUpDown aria-hidden className="h-3 w-3 opacity-50" />
                      ) : descending ? (
                        <ArrowDown aria-hidden className="h-3 w-3" />
                      ) : (
                        <ArrowUp aria-hidden className="h-3 w-3" />
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const key = rowKey(row);
              const isSelected = selection?.selected.has(key) ?? false;
              return (
                <tr
                  key={key}
                  aria-selected={isSelected || undefined}
                  data-testid={`${testIdPrefix}-${key}`}
                  className={cn(onRowClick !== undefined && 'cursor-pointer')}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  onKeyDown={
                    onRowClick === undefined
                      ? undefined
                      : (event) => {
                          if (event.key === 'Enter') onRowClick(row);
                        }
                  }
                  tabIndex={onRowClick === undefined ? undefined : 0}
                >
                  {selection !== undefined && (
                    <td className="px-2" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select row ${key}`}
                        checked={isSelected}
                        disabled={selection.isSelectable !== undefined && !selection.isSelectable(row)}
                        onChange={() => {
                          const next = new Set<string>(selection.selected);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          selection.onChange(next);
                        }}
                        className="h-3.5 w-3.5 cursor-pointer accent-interactive"
                        data-testid={`select-${key}`}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(column.numeric === true && 'numeric', column.className)}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {footer !== undefined && <tfoot>{footer}</tfoot>}
        </table>
      </div>

      {/* ── Cards, below md ──────────────────────────────────────────── */}
      <ul className="space-y-2 tablet:hidden">
        {sorted.map((row) => {
          const key = rowKey(row);
          const visible = columns.filter((column) => column.hideOnMobile !== true);
          const [primary, ...others] = visible;
          return (
            <li key={key}>
              <div
                className="surface-card p-3"
                data-testid={`${testIdPrefix}-card-${key}`}
                role={onRowClick === undefined ? undefined : 'button'}
                tabIndex={onRowClick === undefined ? undefined : 0}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                onKeyDown={
                  onRowClick === undefined
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter') onRowClick(row);
                      }
                }
              >
                {primary !== undefined && (
                  <div className="mb-2 text-row font-medium text-content">{primary.cell(row)}</div>
                )}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {others.map((column) => (
                    <div key={column.key} className="min-w-0">
                      <dt className="text-body uppercase tracking-[0.04em] text-content-tertiary">
                        {column.header}
                      </dt>
                      <dd className={cn('truncate text-body text-content', column.numeric === true && 'tabular')}>
                        {column.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
